import clsx from 'clsx';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { useEffect, useRef, useState } from 'react';
import { loadPdfJs } from '../pdfJsLoader';

/** Renders verified bytes. Load once and render one page at a time. */
export function CheatsheetPdfPreview({
  blob,
  expectedPages,
}: {
  blob: Blob;
  expectedPages?: number;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [document, setDocument] = useState<PDFDocumentProxy | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  useEffect(() => {
    let cancelled = false;
    let loaded: PDFDocumentProxy | undefined;
    setDocument(null);
    setPageNumber(1);
    setError('');
    setLoading(true);
    void (async () => {
      const pdfJs = await loadPdfJs();
      const bytes = await blob.arrayBuffer();
      if (cancelled) return;
      loaded = await pdfJs.getDocument({ data: bytes, isEvalSupported: false }).promise;
      if (cancelled) {
        void loaded.destroy();
        return;
      }
      if (
        loaded.numPages < 1 ||
        (expectedPages !== undefined && loaded.numPages !== expectedPages)
      ) {
        throw new Error('PDF page count does not match the verified catalogue.');
      }
      setDocument(loaded);
    })().catch((reason: unknown) => {
      void loaded?.destroy();
      if (!cancelled) {
        console.error('Cheatsheet PDF preview failed', reason);
        setError('Preview could not load. You can still download the verified PDF.');
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
      void loaded?.destroy();
    };
  }, [blob, expectedPages]);
  useEffect(() => {
    if (!document) return;
    let cancelled = false;
    let render: { cancel(): void; promise: Promise<void> } | undefined;
    setLoading(true);
    setError('');
    void (async () => {
      const page = await document.getPage(pageNumber);
      if (cancelled || !canvas.current) return;
      const viewport = page.getViewport({ scale: Math.min(2.8, 1.8 * zoom) });
      canvas.current.width = viewport.width;
      canvas.current.height = viewport.height;
      const context = canvas.current.getContext('2d');
      if (!context) throw new Error('PDF preview is unavailable.');
      render = page.render({ canvas: canvas.current, canvasContext: context, viewport });
      await render.promise;
      if (!cancelled) setLoading(false);
    })().catch((reason: unknown) => {
      if (!cancelled) {
        console.error('Cheatsheet PDF page render failed', reason);
        setError('This page could not render. Try another page or download the verified PDF.');
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
      render?.cancel();
    };
  }, [document, pageNumber, zoom]);
  const control =
    'min-h-11 rounded-xl border border-hairline bg-canvas-pure px-3 py-2 text-sm font-bold text-ink disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-coral';
  return (
    <div
      className="rounded-2xl border border-hairline bg-canvas-pure p-3 sm:p-6"
      aria-busy={loading}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm font-bold text-ink">
          Zoom
          <select
            aria-label="PDF preview zoom"
            value={zoom}
            onChange={(event) => setZoom(Number(event.target.value))}
            className={control}
          >
            <option value="1">Fit width</option>
            <option value="1.5">150%</option>
            <option value="2">200%</option>
          </select>
        </label>
        {document && document.numPages > 1 && (
          <nav aria-label="PDF pages" className="flex max-w-full flex-wrap items-center gap-2">
            <button
              type="button"
              className={control}
              disabled={loading || pageNumber === 1}
              onClick={() => setPageNumber((number) => number - 1)}
            >
              Previous
            </button>
            <label className="flex items-center gap-2 text-sm font-bold text-ink">
              Page
              <select
                aria-label="PDF page"
                value={pageNumber}
                disabled={loading}
                onChange={(event) => setPageNumber(Number(event.target.value))}
                className={control}
              >
                {Array.from({ length: document.numPages }, (_, index) => (
                  <option key={index + 1} value={index + 1}>
                    {index + 1} of {document.numPages}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className={control}
              disabled={loading || pageNumber === document.numPages}
              onClick={() => setPageNumber((number) => number + 1)}
            >
              Next
            </button>
          </nav>
        )}
      </div>
      {loading && (
        <p role="status" className="mb-3 text-sm font-bold text-slate2">
          Rendering your print preview…
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm font-bold text-ink">
          {error}
        </p>
      )}
      <div className="overflow-auto">
        <canvas
          ref={canvas}
          style={{ width: String(100 * zoom) + '%', maxWidth: zoom === 1 ? '1400px' : 'none' }}
          role="img"
          aria-label={
            'PDF print preview, page ' +
            pageNumber +
            ' of ' +
            (document?.numPages ?? '…') +
            ', with Airbotix logo'
          }
          className={clsx('mx-auto h-auto w-full max-w-[1400px] bg-canvas-pure shadow-card-soft', {
            hidden: !!error,
            invisible: loading && !error,
          })}
        />
      </div>
    </div>
  );
}
